import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Input from "../../../../componentes/input/input";
import InputFormat from "../../../../componentes/inputMask/inputMask";
import Button from "../../../../componentes/button/button";
import NovaSolicitacaoLayout from "../shared/NovaSolicitacaoLayout";
import { useRascunhoFormulario } from "../../Rascunho/RascunhoContext";
import { listaDoRascunho } from "../../../../utils/solicitacoes/rascunhoStorage";
import GdmDadosCampos from "../../Shared/GdmDadosCampos";
import StatusBadge from "../../Shared/StatusBadge";
import AssinaturaCanvas from "../../../meuUsuario/AssinaturaCanvas";
import { enviarAnexosStaged, enviarAposAnexos, renomearAnexosPorTipo, tiposAnexoFaltando } from "../../../../utils/solicitacoes/anexosStaged";
import { enviarComentariosStaged } from "../../../../utils/solicitacoes/comentariosStaged";
import { formatarData } from "../../../../utils/solicitacoes/formatters";

const CODIGO_CLIENTE_REGEX = /^\d{4}-\d{4}$/;

/**
 * Mesma lista do backend (EtapaRenovacaoGdmSolicitacaoHandler), que é quem valida de fato no envio.
 * Cada documento pode ser marcado como não enviado, com motivo. A assinatura do cliente é opcional.
 */
const CONTRATO = "Contrato";
export const DOCUMENTOS_OBRIGATORIOS_RENOVACAO_GDM = ["Documento do Responsável", "Comprovante de Endereço", CONTRATO];
export const ASSINATURA_CLIENTE = "Assinatura do Cliente";

// Rótulos locais (o processoRegistry importa este formulário — importar dele aqui criaria ciclo).
export const ETAPAS_RENOVACAO_GDM = [
    { codigo: "ETAPA_1_SOLICITACAO", label: "Solicitação" },
    { codigo: "ETAPA_2_APROVACAO", label: "Aprovação da Solicitação" },
    { codigo: "ETAPA_3_CONFIRMACAO_RENOVACAO", label: "Confirmação de Renovação" },
];
const etapaLabel = codigo => ETAPAS_RENOVACAO_GDM.find(e => e.codigo === codigo)?.label;

const situacaoRenovacao = r => r.renovado === true ? "Renovada" : r.renovado === false ? "Não renovada" : null;

export default function NovaSolicitacaoRenovacaoGdmForm({ service, onCriada, encaminhar }) {
    const [clienteCodigo, setClienteCodigo] = useState("");
    const [consulta, setConsulta] = useState(null);
    const [consultando, setConsultando] = useState(false);
    const [baseGdmId, setBaseGdmId] = useState(null);
    // Por documento obrigatório: não enviado? { [documento]: motivo } — presença da chave = marcado.
    const [naoEnviados, setNaoEnviados] = useState({});
    // Assinatura desenhada na tela (File PNG) — opcional.
    const [assinatura, setAssinatura] = useState(null);
    const [assinaturaUrl, setAssinaturaUrl] = useState(null);
    const [anexos, setAnexos] = useState([]);
    const [comentarios, setComentarios] = useState([]);

    // Rascunho (botão "Salvar rascunho" da página) — só campos; anexos e assinatura desenhada ficam de fora.
    useRascunhoFormulario(
        () => ({ clienteCodigo, baseGdmId, naoEnviados, comentarios }),
        dados => {
            const texto = valor => (typeof valor === "string" ? valor : "");
            setNaoEnviados(dados.naoEnviados && typeof dados.naoEnviados === "object" && !Array.isArray(dados.naoEnviados)
                ? Object.fromEntries(Object.entries(dados.naoEnviados)
                    .filter(([doc, motivo]) => DOCUMENTOS_OBRIGATORIOS_RENOVACAO_GDM.includes(doc) && typeof motivo === "string"))
                : {});
            setAssinatura(null);
            setComentarios(listaDoRascunho(dados.comentarios));
            // Consulta o cliente de novo (dados da base podem ter mudado) e reaplica a GDM escolhida.
            aoMudarCodigoCliente(texto(dados.clienteCodigo), typeof dados.baseGdmId === "number" ? dados.baseGdmId : null);
        },
    );
    const [enviando, setEnviando] = useState(false);

    const cliente = consulta?.cliente;
    const gdms = consulta?.gdms || [];
    const renovacoesAnteriores = consulta?.renovacoesAnteriores || [];
    const emAndamento = renovacoesAnteriores.filter(r => r.status === "ABERTA" || r.status === "EM_ANDAMENTO");
    // Documento marcado como não enviado (com motivo) deixa de ser exigido — mesma regra do backend.
    const documentosExigidos = DOCUMENTOS_OBRIGATORIOS_RENOVACAO_GDM.filter(doc => !(doc in naoEnviados));

    useEffect(() => {
        if (!assinatura) {
            setAssinaturaUrl(null);
            return undefined;
        }
        const url = URL.createObjectURL(assinatura);
        setAssinaturaUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [assinatura]);

    function alternarNaoEnviado(documento) {
        setNaoEnviados(prev => {
            const proximo = { ...prev };
            if (documento in proximo) delete proximo[documento];
            else proximo[documento] = "";
            return proximo;
        });
    }

    // `baseGdmIdDesejado`: ao retomar um rascunho, volta a selecionar a GDM escolhida — se ela ainda
    // existir na base (pode ter sido reimportada desde então).
    function aoMudarCodigoCliente(valor, baseGdmIdDesejado = null) {
        const codigo = valor || "";
        setClienteCodigo(codigo);
        setConsulta(null);
        setBaseGdmId(null);
        if (!CODIGO_CLIENTE_REGEX.test(codigo)) return;

        setConsultando(true);
        service.consultarCliente(codigo)
            .then(res => {
                setConsulta(res.data);
                if (!res.data.cliente?.encontrado) toast.warn("Cliente não encontrado no cadastro.");
                const gdms = res.data.gdms || [];
                if (baseGdmIdDesejado && gdms.some(g => g.id === baseGdmIdDesejado)) setBaseGdmId(baseGdmIdDesejado);
                // Uma GDM só: já fica escolhida.
                else if (gdms.length === 1) setBaseGdmId(gdms[0].id);
            })
            .catch(() => toast.error("Erro ao consultar o cliente."))
            .finally(() => setConsultando(false));
    }

    function validar() {
        if (!CODIGO_CLIENTE_REGEX.test(clienteCodigo)) {
            toast.error("Informe o código do cliente no formato 9999-9999.");
            return false;
        }
        if (!cliente?.encontrado) {
            toast.error("Cliente não encontrado no cadastro — confira o código.");
            return false;
        }
        if (gdms.length > 0 && !baseGdmId) {
            toast.error("Selecione a GDM que será renovada.");
            return false;
        }
        const semMotivo = Object.entries(naoEnviados).filter(([, motivo]) => !motivo.trim()).map(([doc]) => doc);
        if (semMotivo.length > 0) {
            toast.error(`Informe o motivo de não enviar: ${semMotivo.join(", ")}.`);
            return false;
        }
        const faltando = tiposAnexoFaltando(documentosExigidos, anexos);
        if (faltando.length > 0) {
            toast.error(`Anexe: ${faltando.join(", ")}. Se não for possível, marque como não enviado e informe o motivo na seção de documentos.`);
            return false;
        }
        return true;
    }

    async function enviar() {
        if (!validar()) return;

        let usuarioEscolhidoId;
        try {
            usuarioEscolhidoId = await encaminhar("ETAPA_2_APROVACAO");
        } catch {
            return;
        }

        setEnviando(true);
        // O backend cria sem enviar (ver RenovacaoGdmController#criar): sobe anexos/comentários e só
        // então envia — é nesse envio que ele confere os documentos obrigatórios.
        service.criarSolicitacao({
            clienteCodigo,
            baseGdmId,
            documentosNaoEnviados: Object.entries(naoEnviados).map(([documento, motivo]) => ({ documento, motivo: motivo.trim() })),
            usuarioEscolhidoId,
        })
            .then(async res => {
                // A assinatura sobe como um anexo comum ("Assinatura do Cliente").
                const anexosEnvio = assinatura ? [...anexos, { arquivo: assinatura, descricao: ASSINATURA_CLIENTE }] : anexos;
                const [{ falha: falhaAnexos }, { falha: falhaComentarios }] = await Promise.all([
                    // Documentos obrigatórios e a assinatura sobem como "CODIGO_CLIENTE - Documento.ext".
                    enviarAnexosStaged(res.data.id, renomearAnexosPorTipo(anexosEnvio, [...DOCUMENTOS_OBRIGATORIOS_RENOVACAO_GDM, ASSINATURA_CLIENTE], clienteCodigo)),
                    enviarComentariosStaged(res.data.id, comentarios),
                ]);
                if (falhaAnexos.length > 0 || falhaComentarios.length > 0) {
                    const partes = [];
                    if (falhaAnexos.length > 0) partes.push(`${falhaAnexos.length} anexo(s) (${falhaAnexos.map(f => `${f.arquivo.name}: ${f.erro}`).join(", ")})`);
                    if (falhaComentarios.length > 0) partes.push(`${falhaComentarios.length} comentário(s)`);
                    toast.error(`Solicitação criada, mas houve falha ao enviar: ${partes.join(" e ")}.`);
                }
                const enviada = await enviarAposAnexos(res.data, usuarioEscolhidoId);
                if (enviada) toast.success("Solicitação de renovação de GDM criada com sucesso!");
                onCriada(enviada || res.data);
            })
            .catch(error => console.error("Erro ao criar solicitação de renovação de GDM", error))
            .finally(() => setEnviando(false));
    }

    return (
        <NovaSolicitacaoLayout
            anexos={anexos}
            onAnexosChange={setAnexos}
            comentarios={comentarios}
            onComentariosChange={setComentarios}
            anexosObrigatorios={documentosExigidos}
            sugestoes={[...DOCUMENTOS_OBRIGATORIOS_RENOVACAO_GDM, "Outros"]}
        >
            <h3 className="text-sm font-semibold text-foreground">Cliente</h3>
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
                <InputFormat
                    label="Código do Cliente"
                    required
                    mask="9999-9999"
                    value={clienteCodigo}
                    onChange={aoMudarCodigoCliente}
                    placeholder="0000-0000"
                />
                <div className="sm:col-span-2">
                    <Input label="Razão Social" disabled value={cliente?.razaoSocial || ""} onChange={() => { }} />
                </div>
                <div className="sm:col-span-2">
                    <Input label="Nome Fantasia" disabled value={cliente?.fantasia || ""} onChange={() => { }} />
                </div>
            </div>
            {consultando && <p className="text-xs text-muted-foreground -mt-2">Consultando cliente e base de GDM...</p>}
            {consulta && !cliente?.encontrado && (
                <p className="text-xs text-destructive -mt-2">Cliente não encontrado no cadastro — não é possível solicitar a renovação.</p>
            )}

            <div className="flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5">
                <i className="fa fa-paperclip text-amber-600 text-sm mt-0.5 shrink-0" />
                <div className="text-sm text-amber-900 flex-1 flex flex-col gap-2">
                    <div>
                        <strong>Documentos obrigatórios</strong>
                        <span className="block text-xs">
                            Anexe com exatamente essas descrições (os arquivos são enviados como "{clienteCodigo || "CÓDIGO"} - Documento").
                            Se algum não puder ser enviado, marque e informe o motivo.
                        </span>
                    </div>
                    {DOCUMENTOS_OBRIGATORIOS_RENOVACAO_GDM.map(doc => {
                        const marcado = doc in naoEnviados;
                        return (
                            <div key={doc} className="flex flex-col gap-1.5 border-t border-amber-200 pt-2">
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                                    <span className="font-semibold">{doc}</span>
                                    <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer select-none">
                                        <input type="checkbox" checked={marcado} onChange={() => alternarNaoEnviado(doc)} />
                                        {doc === CONTRATO ? "Contrato não enviado" : "Cliente não quis compartilhar"}
                                    </label>
                                </div>
                                {marcado && (
                                    <Input
                                        label="Motivo do não envio"
                                        required
                                        value={naoEnviados[doc]}
                                        onChange={e => setNaoEnviados(prev => ({ ...prev, [doc]: e.target.value }))}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold text-foreground">
                    Assinatura do cliente <span className="text-xs font-normal text-muted-foreground">(opcional)</span>
                </h3>
                {assinatura ? (
                    <div className="flex flex-col gap-2">
                        <div className="h-32 w-full max-w-sm rounded-lg border border-border bg-white p-2 flex items-center justify-center">
                            {assinaturaUrl && <img src={assinaturaUrl} alt="Assinatura do cliente" className="max-h-full max-w-full object-contain" />}
                        </div>
                        <div>
                            <Button variant="outline" label="Remover / refazer" iconLeft="fa fa-undo" onClick={() => setAssinatura(null)} />
                        </div>
                    </div>
                ) : (
                    <div className="max-w-xl">
                        <AssinaturaCanvas
                            onSalvar={arquivo => {
                                setAssinatura(arquivo);
                                return Promise.resolve();
                            }}
                        />
                    </div>
                )}
            </div>

            {consulta && cliente?.encontrado && (
                <>
                    <h3 className="text-sm font-semibold text-foreground mt-2">
                        GDM do cliente na base {gdms.length > 0 && <span className="text-muted-foreground font-normal">({gdms.length})</span>}
                    </h3>
                    {gdms.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Nenhuma GDM encontrada na base para este cliente.</p>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {gdms.length > 1 && <p className="text-xs text-muted-foreground">Selecione a GDM que será renovada.</p>}
                            {gdms.map(item => {
                                const selecionada = baseGdmId === item.id;
                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        aria-pressed={selecionada}
                                        onClick={() => setBaseGdmId(item.id)}
                                        className={`text-left rounded-lg border p-3 transition-colors ${selecionada
                                            ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                                            : "border-border hover:bg-muted"}`}
                                    >
                                        <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-foreground">
                                            <i className={selecionada ? "fa fa-check-circle text-primary" : "far fa-circle text-muted-foreground"} />
                                            {item.gdm?.gdmDescricao || item.gdm?.gdmCodigo || "GDM"}
                                            {item.gdm?.numeroSerie && <span className="text-muted-foreground font-normal">— Série {item.gdm.numeroSerie}</span>}
                                        </div>
                                        <GdmDadosCampos gdm={item.gdm} />
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    <h3 className="text-sm font-semibold text-foreground mt-2">Renovações já solicitadas para o cliente</h3>
                    {emAndamento.length > 0 && (
                        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2">
                            <i className="fa fa-exclamation-triangle mr-1" />
                            Já existe renovação em andamento para este cliente: {emAndamento.map(r => r.numero).join(", ")}.
                        </p>
                    )}
                    {renovacoesAnteriores.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Nenhuma renovação anterior.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-border text-sm">
                                <thead>
                                    <tr className="text-left text-xs text-muted-foreground uppercase">
                                        <th className="py-1.5 pr-3">Nº</th>
                                        <th className="py-1.5 pr-3">Data</th>
                                        <th className="py-1.5 pr-3">Nº de série</th>
                                        <th className="py-1.5 pr-3">Etapa</th>
                                        <th className="py-1.5 pr-3">Resultado</th>
                                        <th className="py-1.5 pr-3">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {renovacoesAnteriores.map(r => (
                                        <tr
                                            key={r.solicitacaoId}
                                            className="cursor-pointer hover:bg-muted"
                                            title="Abrir em outra aba"
                                            onClick={() => window.open(`/solicitacoes/${r.solicitacaoId}`, "_blank", "noopener,noreferrer")}
                                        >
                                            <td className="py-1.5 pr-3 font-medium text-primary">{r.numero}</td>
                                            <td className="py-1.5 pr-3">{formatarData(r.dataSolicitacao)}</td>
                                            <td className="py-1.5 pr-3">{r.numeroSerie || "-"}</td>
                                            <td className="py-1.5 pr-3">{etapaLabel(r.etapaAtual) || "-"}</td>
                                            <td className="py-1.5 pr-3">{situacaoRenovacao(r) || "-"}</td>
                                            <td className="py-1.5 pr-3"><StatusBadge status={r.status} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            <div className="flex justify-end">
                <Button variant="blue" label="Solicitar renovação" iconLeft="fa fa-paper-plane" loading={enviando} onClick={enviar} />
            </div>
        </NovaSolicitacaoLayout>
    );
}
